import { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { TEST_IDS, ASSETS } from "@/constants/testIds";

const NavLink = ({ href, label, testId, onClick }) => (
    <a
        href={href}
        data-testid={testId}
        onClick={onClick}
        className="text-[13px] tracking-[0.14em] uppercase font-medium text-ink/80 hover:text-navy transition-colors duration-300"
    >
        {label}
    </a>
);

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const close = () => setOpen(false);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-hairline"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <a
                        href="#top"
                        data-testid={TEST_IDS.headerLogo}
                        className="flex items-center gap-3 group"
                        aria-label="Southern African Traders Ltd home"
                    >
                        <img
                            src={ASSETS.logoCircle}
                            alt="Southern African Traders Ltd logo"
                            className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover ring-1 ring-hairline"
                        />
                        <span className="hidden sm:flex flex-col leading-tight">
                            <span className="font-serif text-[15px] md:text-base font-semibold text-ink tracking-tight">
                                Southern African Traders
                            </span>
                            <span className="text-[10px] tracking-[0.3em] uppercase text-muted2">
                                Ltd · Digital Ventures
                            </span>
                        </span>
                    </a>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-10">
                        <NavLink href="#about" label="About" testId={TEST_IDS.navAbout} />
                        <NavLink
                            href="#platforms"
                            label="Our Platforms"
                            testId={TEST_IDS.navPlatforms}
                        />
                        <NavLink
                            href="#coming-soon"
                            label="Coming Soon"
                            testId={TEST_IDS.navComingSoon}
                        />
                        <a
                            href="#platforms"
                            data-testid={TEST_IDS.headerCta}
                            className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-medium text-navy hover:text-navy-900 border-b border-navy/40 hover:border-navy pb-1 transition-colors duration-300"
                        >
                            Explore Our Platforms
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                    </nav>

                    {/* Mobile toggle */}
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        data-testid={TEST_IDS.mobileMenuToggle}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 text-ink hover:text-navy transition-colors"
                    >
                        {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid={TEST_IDS.mobileMenu}
                    className="md:hidden bg-white border-t border-hairline"
                >
                    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
                        <NavLink
                            href="#about"
                            label="About"
                            testId={TEST_IDS.mobileNavAbout}
                            onClick={close}
                        />
                        <NavLink
                            href="#platforms"
                            label="Our Platforms"
                            testId={TEST_IDS.mobileNavPlatforms}
                            onClick={close}
                        />
                        <NavLink
                            href="#coming-soon"
                            label="Coming Soon"
                            testId={TEST_IDS.mobileNavComingSoon}
                            onClick={close}
                        />
                        <a
                            href="#platforms"
                            data-testid={TEST_IDS.mobileHeaderCta}
                            onClick={close}
                            className="inline-flex w-fit items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-medium text-navy border-b border-navy/40 pb-1"
                        >
                            Explore Our Platforms
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
